import React from 'react'
import { Message } from 'rsuite'
import { ArtifactPolymorphic } from '../api/generated/models'
import { ReportItem } from '../components/reports/types/ReportTypes'
import type { ArtifactReport } from '../api/generated/models/artifactReport'
import { PatchedImpactArtifactRequest } from '../api/generated/models/patchedImpactArtifactRequest'
import { PatchedOptimizeArtifactRequest } from '../api/generated/models/patchedOptimizeArtifactRequest'

interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
}

interface SaveModelAndReportResponse {
  success: boolean
  data?: {
    model: ArtifactPolymorphic
    report: ReportItem
  }
  error?: string
}

// Type for the pushToast function
type PushToastFunction = (
  message: React.ReactElement,
  options: { placement: string; duration: number },
) => void

const normalizeOptimizeRevenuePerKpi = (
  modelData: PatchedImpactArtifactRequest | PatchedOptimizeArtifactRequest,
) => {
  const optimizeConfig = (modelData as PatchedOptimizeArtifactRequest).optimize_config

  if (!optimizeConfig) return

  const revenuePerKpiId = optimizeConfig.revenue_per_kpi_id

  if (revenuePerKpiId == null) {
    optimizeConfig.revenue_per_kpi_id = null
  }

  const isRevenue = optimizeConfig.dependent_driver_is_revenue

  if (isRevenue === true || revenuePerKpiId != null) {
    optimizeConfig.expected_media_contribution = null
  }

  if (isRevenue === false) {
    optimizeConfig.roi_impression = null
    optimizeConfig.roi_reach = null
  }
}

// Save a new model using the existing artifacts API
export const saveNewModel = async (
  modelData: Partial<ArtifactPolymorphic>,
  solution: string,
): Promise<boolean> => {
  try {
    // Using the existing artifacts API for model operations
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/artifacts/${solution}/`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-type': 'application/json',
        },
        body: JSON.stringify(modelData),
      },
    )

    if (response.ok) {
      return true
    } else {
      throw new Error('Failed to save model.')
    }
  } catch (error) {
    console.error(error)
    return false
  }
}

// Save a new model and report using the existing artifacts API
export const saveNewModelAndReport = async (
  reportData: Partial<ArtifactReport>,
  title: string,
): Promise<SaveModelAndReportResponse> => {
  try {
    // Using the existing artifact created by running model predict
    const response = await fetch(
      // TODO: Remove hard coded impact
      `${process.env.REACT_APP_API_URL}/api/artifacts/${reportData.solution?.toLowerCase()}/${reportData.artifact_id}/`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-type': 'application/json',
        },
        body: JSON.stringify({ client_id: reportData.client_id, name: title }),
      },
    )

    if (response.ok) {
      const res = await response.json()
      const modelData = res.data
      // Exclude slug from reportData to avoid conflicts (report will get its own slug)
      const { slug, ...reportDataWithoutSlug } = reportData
      const payload = {
        ...reportDataWithoutSlug,
        artifact_id: modelData.id,
        name: modelData.name,
        client_id: reportData.client_id, // Explicitly include client_id after spread to ensure it's not undefined
        client: reportData.client,
      }
      const reportResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/reports`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      )

      if (reportResponse.ok) {
        const reportRes = await reportResponse.json()
        // Return both model and report data in a structured format
        return {
          success: true,
          data: {
            model: modelData,
            report: reportRes.data,
          },
        }
      }
    }

    throw new Error('Failed to save model or report.')
  } catch (error) {
    console.error(error)
    return {
      success: false,
      error: (error as Error).message || 'Failed to save model or report',
    }
  }
}

// Update an existing model using the existing artifacts API
export const updateModel = async (
  modelId: number,
  modelData: PatchedImpactArtifactRequest | PatchedOptimizeArtifactRequest,
  solution: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (solution === 'optimize') {
      normalizeOptimizeRevenuePerKpi(modelData)
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/artifacts/${solution}/${modelId}/`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-type': 'application/json',
        },
        body: JSON.stringify(modelData),
      },
    )

    if (response.ok) {
      return { success: true }
    } else {
      const errorData = await response.json()
      // Extract error message from backend response
      let errorMessage = 'Failed to update model.'
      if (errorData.message) {
        if (typeof errorData.message === 'string') {
          errorMessage = errorData.message
        } else if (errorData.message.non_field_errors) {
          errorMessage = errorData.message.non_field_errors.join('\n')
        } else {
          errorMessage = JSON.stringify(errorData.message)
        }
      }
      return { success: false, error: errorMessage }
    }
  } catch (error) {
    console.error(error)
    return { success: false, error: (error as Error).message || 'Failed to update model' }
  }
}

// Replace an original artifact with an explore variant using the new replace endpoint
// This is used when user saves changes after running in EDIT_MODEL (explore) mode
export const replaceArtifact = async (
  oldArtifactId: number,
  newArtifactId: number,
  solution: string,
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/artifacts/${solution}/replace/`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-type': 'application/json',
        },
        body: JSON.stringify({
          old_artifact_id: oldArtifactId,
          new_artifact_id: newArtifactId,
        }),
      },
    )

    if (response.ok) {
      const data = await response.json()
      return { success: true, data: data.data }
    } else {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to replace artifact.')
    }
  } catch (error) {
    console.error('Error replacing artifact:', error)
    return {
      success: false,
      error: (error as Error).message || 'Failed to replace artifact',
    }
  }
}

// Save a new report - this will be implemented by the backend later
export const saveNewReport = async (
  reportData: Partial<ArtifactReport>,
): Promise<ApiResponse> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/artifacts/reports/`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-type': 'application/json',
        },
        body: JSON.stringify(reportData),
      },
    )

    if (response.ok) {
      const responseData = await response.json()
      return { success: true, data: responseData.data }
    } else {
      throw new Error('Failed to save report.')
    }
  } catch (error) {
    console.error(error)
    return {
      success: false,
      error: (error as Error).message || 'Failed to save new report',
    }
  }
}

// Update an existing report - this will be implemented by the backend later
export const updateReport = async (
  reportId: number,
  reportData: Partial<ArtifactReport>,
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/artifacts/reports/${reportId}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-type': 'application/json',
        },
        body: JSON.stringify(reportData),
      },
    )

    if (response.ok) {
      return true
    } else {
      throw new Error('Failed to update model.')
    }
  } catch (error) {
    console.error(error)
    return false
  }
}

// Delete an existing model (using the artifacts API)
export const deleteModel = async (
  model: any,
  pushToast: PushToastFunction,
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/artifacts/${model.solution.toLowerCase()}/${model.id}/`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      },
    )

    if (response.ok) {
      pushToast(<Message type="success">Model deleted successfully.</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })
      return true
    } else {
      throw new Error('Failed to delete model.')
    }
  } catch (error) {
    console.error(error)
    pushToast(
      <Message type="error">
        {(error as Error).message || 'Failed to delete model'}
      </Message>,
      { placement: 'topCenter', duration: 5000 },
    )
    return false
  }
}

// Delete an existing report - this will be implemented by the backend later
export const deleteReport = async (
  reportId: number,
  pushToast: PushToastFunction,
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/artifacts/reports/${reportId}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      },
    )

    if (response.ok) {
      pushToast(
        <Message type="success">Report deleted successfully.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return true
    } else {
      throw new Error('Failed to delete report.')
    }
  } catch (error) {
    console.error(error)
    pushToast(
      <Message type="error">
        {(error as Error).message || 'Failed to delete report'}
      </Message>,
      { placement: 'topCenter', duration: 5000 },
    )
    return false
  }
}
